(() => {
  const PRODUCT_CARD_SELECTOR = '.nini-product-card, .nini-mobile-product-card, [data-nini-product-card]';
  const PRODUCT_CARD_IMAGE_SELECTOR = '.nini-product-card__image, .nini-mobile-product-card__image';
  const VARIANT_THUMB_SELECTOR = '[data-nini-variant-thumb], .nini-variant-thumbs__item';
  let variantThumbDelegationReady = false;
  const productJsonCache = new Map();

  const normalizeImageUrl = (value) => {
    if (!value) return '';
    return value.startsWith('//') ? `https:${value}` : value;
  };

  const normalizeSrcset = (value) => {
    if (!value) return '';
    return value.replace(/(^|,\s*)\/\//g, '$1https://');
  };

  const imageUrlWithWidth = (value, width) => {
    const url = normalizeImageUrl(value);
    if (!url) return '';
    if (url.includes('width=')) return url.replace(/([?&])width=\d+/g, `$1width=${width}`);
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const getProductCards = (scope) => {
    const root = scope || document;
    const cards = [];

    if (root.matches?.(PRODUCT_CARD_SELECTOR)) {
      cards.push(root);
    }

    root.querySelectorAll?.(PRODUCT_CARD_SELECTOR).forEach((card) => cards.push(card));

    return cards;
  };

  const getVariantThumbs = (card) => (
    Array.from(card.querySelectorAll(VARIANT_THUMB_SELECTOR))
      .filter((thumb) => thumb.hasAttribute('data-nini-variant-thumb') || thumb.querySelector('img'))
  );

  const getThumbImage = (thumb) => thumb.querySelector('img');

  const getThumbMainSrc = (thumb) => (
    normalizeImageUrl(thumb.getAttribute('data-nini-main-src'))
    || imageUrlWithWidth(getThumbImage(thumb)?.currentSrc || getThumbImage(thumb)?.src, 720)
  );

  const getThumbMainSrcset = (thumb) => {
    const srcset = normalizeSrcset(thumb.getAttribute('data-nini-main-srcset'));
    if (srcset) return srcset;

    const thumbSrc = getThumbImage(thumb)?.currentSrc || getThumbImage(thumb)?.src;
    if (!thumbSrc) return '';

    return [360, 540, 720, 900]
      .map((width) => `${imageUrlWithWidth(thumbSrc, width)} ${width}w`)
      .join(', ');
  };

  const normalizeProductImageKey = (value) => (
    normalizeImageUrl(value)
      .replace(/([?&])width=\d+/g, '')
      .replace(/[?&]$/, '')
  );

  const productJsonUrlForCard = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';

    const url = new URL(link.getAttribute('href'), window.location.href);
    const proxyMatch = url.pathname.match(/^\/proxy\/(mobile|desktop)(\/.*)$/);
    const proxyPrefix = proxyMatch ? `/proxy/${proxyMatch[1]}` : '';
    const productPath = proxyMatch ? proxyMatch[2] : url.pathname;

    if (!productPath.includes('/products/')) return '';

    const cleanPath = productPath.replace(/\/$/, '').replace(/\.js$/, '');
    return `${proxyPrefix}${cleanPath}.js`;
  };

  const fetchProductJson = (url) => {
    if (!url) return Promise.resolve(null);
    if (!productJsonCache.has(url)) {
      productJsonCache.set(
        url,
        fetch(url, { credentials: 'same-origin', cache: 'no-store' })
          .then((response) => response.ok ? response.json() : null)
          .catch(() => null),
      );
    }

    return productJsonCache.get(url);
  };

  const collectProductImages = (productData) => {
    const images = [];
    const seen = new Set();
    const pushImage = (src, alt = '') => {
      const normalizedSrc = normalizeImageUrl(src);
      const key = normalizeProductImageKey(normalizedSrc);
      if (!normalizedSrc || seen.has(key)) return;

      seen.add(key);
      images.push({ src: normalizedSrc, alt });
    };

    productData?.variants?.forEach((variant) => {
      pushImage(variant.featured_image?.src, variant.public_title || variant.title || productData.title);
    });
    productData?.images?.forEach((src) => pushImage(src, productData.title));
    pushImage(productData?.featured_image, productData?.title);

    return images;
  };

  const createVariantThumb = (image, isActive = false) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `nini-variant-thumbs__item${isActive ? ' is-active' : ''}`;
    button.setAttribute('data-nini-variant-thumb', '');
    button.setAttribute('data-nini-main-src', imageUrlWithWidth(image.src, 720));
    button.setAttribute(
      'data-nini-main-srcset',
      [360, 540, 720, 900].map((width) => `${imageUrlWithWidth(image.src, width)} ${width}w`).join(', '),
    );
    button.setAttribute('data-nini-main-alt', image.alt || '');
    button.setAttribute('aria-label', `${image.alt || '商品'}の画像を表示`);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

    const img = document.createElement('img');
    img.src = imageUrlWithWidth(image.src, 128);
    img.alt = image.alt || '';
    img.loading = 'lazy';
    button.appendChild(img);

    return button;
  };

  const setActiveVariantThumb = (thumb, card) => {
    getVariantThumbs(card).forEach((item) => {
      const isCurrent = item === thumb;
      item.classList.toggle('is-active', isCurrent);
      item.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
    });
  };

  const swapProductCardImage = (thumb) => {
    const card = thumb.closest(PRODUCT_CARD_SELECTOR);
    if (!card) return false;

    const mainImage = card.querySelector(PRODUCT_CARD_IMAGE_SELECTOR);
    const nextSrc = getThumbMainSrc(thumb);
    const nextSrcset = getThumbMainSrcset(thumb);
    const nextAlt = thumb.getAttribute('data-nini-main-alt') || getThumbImage(thumb)?.alt;

    if (!mainImage || !nextSrc) return false;

    mainImage.src = nextSrc;
    if (nextSrcset) {
      mainImage.srcset = nextSrcset;
    } else {
      mainImage.removeAttribute('srcset');
    }

    if (nextAlt) {
      mainImage.alt = nextAlt;
    }

    setActiveVariantThumb(thumb, card);
    return true;
  };

  const hydrateMissingVariantThumbs = (section = document) => {
    getProductCards(section).forEach((card) => {
      if (card.dataset.niniThumbsHydrated === 'true' || getVariantThumbs(card).length > 0) return;

      const mainImage = card.querySelector(PRODUCT_CARD_IMAGE_SELECTOR);
      const productJsonUrl = productJsonUrlForCard(card);
      if (!mainImage || !productJsonUrl) return;

      card.dataset.niniThumbsHydrated = 'true';

      fetchProductJson(productJsonUrl).then((productData) => {
        const productImages = collectProductImages(productData);
        if (productImages.length < 2 || !card.isConnected) return;

        const activeKey = normalizeProductImageKey(mainImage.currentSrc || mainImage.src);
        const thumbs = document.createElement('div');
        thumbs.className = 'nini-variant-thumbs';
        thumbs.setAttribute('aria-label', '種類のサムネイル');

        productImages.slice(0, 5).forEach((image, index) => {
          const isActive = normalizeProductImageKey(imageUrlWithWidth(image.src, 720)) === activeKey || (index === 0 && !activeKey);
          thumbs.appendChild(createVariantThumb(image, isActive));
        });

        if (!thumbs.querySelector('.is-active')) {
          thumbs.firstElementChild?.classList.add('is-active');
          thumbs.firstElementChild?.setAttribute('aria-pressed', 'true');
        }

        const mobileShell = card.querySelector('.nini-mobile-product-card__media-shell');
        const mediaWrap = card.querySelector('.nini-product-card__media-wrap');
        if (mobileShell) {
          mobileShell.appendChild(thumbs);
        } else if (mediaWrap) {
          mediaWrap.insertAdjacentElement('afterend', thumbs);
        } else {
          card.appendChild(thumbs);
        }

        initVariantThumbs(card);
      });
    });
  };

  const initVariantThumbs = (section = document) => {
    getProductCards(section).forEach((card) => {
      const mainImage = card.querySelector(PRODUCT_CARD_IMAGE_SELECTOR);
      const thumbs = getVariantThumbs(card);

      if (!mainImage || thumbs.length === 0) return;

      thumbs.forEach((thumb) => {
        if (thumb.tagName !== 'BUTTON') {
          thumb.setAttribute('role', 'button');
          thumb.setAttribute('tabindex', '0');
        }

        if (!thumb.hasAttribute('aria-pressed')) {
          thumb.setAttribute('aria-pressed', 'false');
        }
      });

      if (!thumbs.some((thumb) => thumb.classList.contains('is-active'))) {
        thumbs[0].classList.add('is-active');
        thumbs[0].setAttribute('aria-pressed', 'true');
      }
    });

    hydrateMissingVariantThumbs(section);

    if (variantThumbDelegationReady) return;
    variantThumbDelegationReady = true;

    document.addEventListener('click', (event) => {
      const thumb = event.target.closest(VARIANT_THUMB_SELECTOR);
      if (!thumb || !thumb.closest(PRODUCT_CARD_SELECTOR)) return;

      event.preventDefault();
      event.stopPropagation();
      swapProductCardImage(thumb);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const thumb = event.target.closest(VARIANT_THUMB_SELECTOR);
      if (!thumb || !thumb.closest(PRODUCT_CARD_SELECTOR)) return;

      event.preventDefault();
      swapProductCardImage(thumb);
    });
  };

  const initNiniCollection = () => {
    document.querySelectorAll('.nini-collection').forEach((section) => {
      const drawer = section.querySelector('.nini-collection__drawer');
      const grid = section.querySelector('[data-nini-product-grid]');

      section.querySelectorAll('[data-nini-filter-open]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!drawer) return;
          drawer.classList.add('is-open');
          drawer.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('overflow-hidden');
        });
      });

      section.querySelectorAll('[data-nini-filter-close]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!drawer) return;
          drawer.classList.remove('is-open');
          drawer.setAttribute('aria-hidden', 'true');
          document.documentElement.classList.remove('overflow-hidden');
        });
      });

      section.querySelectorAll('[data-nini-view]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!grid) return;
          const view = button.getAttribute('data-nini-view');
          grid.setAttribute('data-view', view);
          try {
            window.localStorage.setItem('nini:collection-view', view);
          } catch (_error) {}
          section.querySelectorAll('[data-nini-view]').forEach((viewButton) => {
            viewButton.classList.toggle('is-active', viewButton === button);
          });
        });
      });

      try {
        const storedView = window.localStorage.getItem('nini:collection-view');
        if (storedView && grid) {
          grid.setAttribute('data-view', storedView);
          section.querySelectorAll('[data-nini-view]').forEach((viewButton) => {
            viewButton.classList.toggle('is-active', viewButton.getAttribute('data-nini-view') === storedView);
          });
        }
      } catch (_error) {}

      initVariantThumbs(section);
    });

    initVariantThumbs(document);
    initMobileRankingMarquee(document);
  };

  const initMobileRankingMarquee = (scope = document) => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduceMotion.matches) return;

    scope.querySelectorAll('.nini-mobile-home__product-row--ranking').forEach((row) => {
      if (row.dataset.niniCatalogRankingReady === 'true') return;

      const originalItems = [...row.children].filter((item) => item.dataset.niniMarqueeClone !== 'true');
      if (originalItems.length < 2) return;

      row.dataset.niniCatalogRankingReady = 'true';
      row.classList.add('is-auto-scroll');

      if (!row.querySelector('[data-nini-marquee-clone="true"]')) {
        originalItems.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.niniMarqueeClone = 'true';
          clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
          row.appendChild(clone);
        });
        initVariantThumbs(row);
      }

      let loopDistance = 0;
      let pauseUntil = 0;
      let virtualLeft = row.scrollLeft;
      const speed = 22 / 1000;

      const updateLoopDistance = () => {
        const firstClone = row.querySelector('[data-nini-marquee-clone="true"]');
        loopDistance = firstClone ? firstClone.offsetLeft - row.firstElementChild.offsetLeft : 0;
      };

      const pauseFor = (duration = 5000) => {
        pauseUntil = performance.now() + duration;
        virtualLeft = row.scrollLeft;
      };

      // setInterval + 常時scrollLeft書き換えはスクロール中のもたつきの原因になるため、
      // requestAnimationFrame(時間ベース) + IntersectionObserver(画面内のみ実行)に変更
      let rafId = null;
      let lastTime = null;

      const tick = (now) => {
        rafId = requestAnimationFrame(tick);
        if (!row.isConnected || loopDistance <= 0) return;
        if (lastTime === null) { lastTime = now; return; }
        const delta = Math.min(now - lastTime, 100);
        lastTime = now;
        if (now < pauseUntil) return;
        virtualLeft += delta * speed;
        if (virtualLeft >= loopDistance) virtualLeft -= loopDistance;
        row.scrollLeft = virtualLeft;
      };

      const start = () => {
        if (rafId === null) {
          lastTime = null;
          virtualLeft = row.scrollLeft;
          rafId = requestAnimationFrame(tick);
        }
      };
      const stop = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      };

      updateLoopDistance();
      if (loopDistance <= 0 || row.scrollWidth <= row.clientWidth + 8) return;

      row.addEventListener('pointerdown', () => pauseFor(), { passive: true });
      row.addEventListener('touchstart', () => pauseFor(), { passive: true });
      row.addEventListener('wheel', () => pauseFor(), { passive: true });
      row.addEventListener('focusin', () => pauseFor());
      window.addEventListener('resize', updateLoopDistance, { passive: true });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') stop();
        else if (row.isConnected) start();
      });

      if ('IntersectionObserver' in window) {
        const marqueeObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && document.visibilityState !== 'hidden') start();
            else stop();
          });
        }, { rootMargin: '80px 0px' });
        marqueeObserver.observe(row);
      } else {
        start();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNiniCollection);
  } else {
    initNiniCollection();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initVariantThumbs(event.target);
    initMobileRankingMarquee(event.target);
  });

  document.addEventListener('nini:product-cards:refresh', (event) => {
    initVariantThumbs(event.detail?.scope || document);
    initMobileRankingMarquee(event.detail?.scope || document);
  });
})();
